import React, { useEffect, useState } from 'react';
import { FiUser, FiAlertTriangle, FiClock, FiHash, FiFileText, FiChevronDown, FiTrendingUp, FiBarChart2, FiGrid } from 'react-icons/fi';
import InsuredTags from './InsuredTags';
import '../../styles/InsuredDetail.css';

interface LineOfBusiness {
  databaseId: string;
  lineOfBusinessName: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NoteRecord = Record<string, any>;

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const PAGE_SIZE = 5;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseListResponse<T>(json: any, key: string): { items: T[]; total: number } {
  const items = Array.isArray(json?.[key]) ? (json[key] as T[]) : [];
  const total = typeof json?.total === 'number' ? json.total : items.length;
  return { items, total };
}

interface PolicyDetail {
  databaseId: string;
  number: string | null;
  isQuote: boolean;
  effectiveDate: string | null;
  expirationDate: string | null;
  bindDate: string | null;
  businessType: string | null;
  businessSubType: string | null;
  description: string | null;
  billingType: string | null;
  insuredDatabaseId: string;
  insuredEmail: string | null;
  insuredFirstName: string | null;
  insuredLastName: string | null;
  insuredCommercialName: string | null;
  carrierName: string | null;
  carrierNAIC: string | null;
  mgaName: string | null;
  totalPremium: number | null;
  totalNonPremium: number | null;
  totalAgencyCommission: number | null;
  changeDate: string | null;
  cancellationDate: string | null;
  active: boolean;
  status: string | null;
  policyTerm: number | null;
  policyTermFormatted: string | null;
  binderId: string | null;
  insuredType: string | null;
  lineOfBusinesses: LineOfBusiness[];
}

const PolicyDetailSidebarPanel: React.FC<{ policyId: string }> = ({ policyId }) => {
  const [data, setData] = useState<PolicyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<NoteRecord[]>([]);
  // Notes the user opened to read in full (list index); cleared when the list reloads.
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [noteLoad, setNoteLoad] = useState(true);
  const [addingNote, setAddingNote] = useState(false);
  const [noteSubject, setNoteSubject] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteTotal, setNoteTotal] = useState(0);
  const [noteSkip,  setNoteSkip]  = useState(0);
  const [noteMoreLoad, setNoteMoreLoad] = useState(false);

  useEffect(() => {
    if (!policyId) return;
    setLoading(true);
    setError(null);
    setData(null);
    setNotes([]);
    setExpandedNotes(new Set());
    setNoteTotal(0); setNoteSkip(0);
    setNoteLoad(true);

    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';

    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url)}/api/ampnowcerts/policydetails?databaseId=${encodeURIComponent(policyId)}&nowCertsToken=${encodeURIComponent(amsToken)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        const item = Array.isArray(json) ? json[0] : json;
        if (!item) throw new Error('No data found');
        setData(item);

        // Notes (non-critical — fail silently). Policy-level: filtered by both insured + policy.
        const insuredDatabaseId = item.insuredDatabaseId;
        if (insuredDatabaseId) {
          fetch(
            `${(process.env.REACT_APP_AccountAMP_Url )}/api/ampnowcerts/insured/${encodeURIComponent(insuredDatabaseId)}/notes?policyDatabaseId=${encodeURIComponent(policyId)}&skip=0&top=${PAGE_SIZE}`,
            { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } }
          )
            .then(r => r.ok ? r.json() : null)
            .then(noteJson => {
              const { items, total } = parseListResponse<NoteRecord>(noteJson, 'notes');
              setNotes(items);
              setNoteTotal(total);
              setNoteSkip(items.length);
            })
            .catch(() => {})
            .finally(() => setNoteLoad(false));
        } else {
          setNoteLoad(false);
        }
      })
      .catch((e) => setError(e.message || 'Failed to load policy details'))
      .finally(() => setLoading(false));
  }, [policyId]);

  const handleAddNote = () => {
    const subject = noteSubject.trim();
    if (!subject || noteSaving || !data?.insuredDatabaseId) return;

    setNoteSaving(true);
    setNoteError(null);

    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';

    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(data.insuredDatabaseId)}/notes`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          NCToken: amsToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subject, policyDatabaseId: policyId }),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        setNotes(prev => [{ subject, changeDate: new Date().toISOString() }, ...prev]);
        setExpandedNotes(new Set());
        setNoteTotal(prev => prev + 1);
        setNoteSkip(prev => prev + 1);
        setNoteSubject('');
        setAddingNote(false);
      })
      .catch(() => setNoteError('Failed to save note.'))
      .finally(() => setNoteSaving(false));
  };

  const handleLoadMoreNotes = () => {
    if (noteMoreLoad || !data?.insuredDatabaseId || notes.length >= noteTotal) return;
    setNoteMoreLoad(true);
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(data.insuredDatabaseId)}/notes?policyDatabaseId=${encodeURIComponent(policyId)}&skip=${noteSkip}&top=${PAGE_SIZE}`,
      { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const { items, total } = parseListResponse<NoteRecord>(json, 'notes');
        setNotes(prev => [...prev, ...items]);
        setNoteTotal(total);
        setNoteSkip(prev => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setNoteMoreLoad(false));
  };

  if (loading) {
    return (
      <div className="isp-skeleton">
        <div className="isp-skeleton__card">
          <div className="isp-skeleton__hero">
            <div className="isp-skeleton__avatar dw-skel dw-skel--circle isp-skeleton__avatar-dim" />
            <div className="isp-skeleton__hero-text">
              <div className="dw-skel isp-skeleton__line--hero-lg" />
              <div className="dw-skel isp-skeleton__line--hero-sm" />
              <div className="dw-skel isp-skeleton__line--hero-badge" />
            </div>
          </div>
          <div className="isp-skeleton__divider" />
          {[1, 2, 3].map(i => (
            <div key={i} className="isp-skeleton__group">
              <div className="dw-skel dw-skel--sm isp-skeleton__label" />
              <div className="isp-skeleton__row">
                <div className="dw-skel isp-skeleton__icon" />
                <div className="dw-skel dw-skel--sm isp-skeleton__line--w70" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="isp__state isp__state--error">
        <div className="isp__state-icon"><FiAlertTriangle size={18} /></div>
        <span className="isp__state-text">{error}</span>
      </div>
    );
  }

  if (!data) return null;

  const clean = (v: string | null | undefined) =>
    !v || v === 'null' || v.trim() === '' ? '' : v.trim();

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const fmtCurrency = (n: number | null) =>
    n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  const insuredName = clean(data.insuredCommercialName) ||
    [clean(data.insuredFirstName), clean(data.insuredLastName)].filter(Boolean).join(' ') ||
    'Unknown';
  const policyNumber = clean(data.number) || data.databaseId.slice(0, 8);
  const isActive = data.active || (data.status || '').toLowerCase() === 'active';
  const lobs = data.lineOfBusinesses?.map(l => l.lineOfBusinessName).filter(Boolean).join(', ') || '—';

  return (
    <div className="isp">
      <div className="isp__card">

        {/* ── Hero ── */}
        <div className="isp__hero">
          <div className="isp__hero-glow isp__hero-glow--policy" />
          <div className="isp__avatar isp__avatar--policy">
            <FiFileText size={18} />
          </div>
          <div className="isp__hero-text">
            <span className="isp__name isp__name--policy">{policyNumber}</span>
            <span className="isp__sub">{insuredName}</span>
            <span className={`isp__badge ${isActive ? 'isp__badge--insured' : 'isp__badge--expired'}`}>
              {data.status || (isActive ? 'Active' : 'Inactive')}
            </span>
          </div>
          <a
            href={`${localStorage.getItem('agencyDomain')}/AMSINS/Policies/Details/${data.databaseId}/Information`}
            target="_blank"
            rel="noopener noreferrer"
            className="isp__hero-redirect"
            title="View in NowCerts"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>

        <InsuredTags policyId={data.databaseId || policyId} />

        <div className="isp__divider" />

        {/* ── Coverage ── */}
        <div className="isp__group">
          <span className="isp__group-label">Coverage</span>
          <div className="isp__row isp__row--static">
            <span className="isp__row-icon isp__row-icon--blue"><FiGrid size={12} /></span>
            <div className="isp__row-kv">
              <span className="isp__kv-label">Line of Business</span>
              <span className="isp__kv-value">{lobs}</span>
            </div>
          </div>
          {clean(data.carrierName) && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon isp__row-icon--blue"><FiUser size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Carrier</span>
                <span className="isp__kv-value">{clean(data.carrierName)}</span>
              </div>
            </div>
          )}
          {clean(data.billingType) && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon isp__row-icon--blue"><FiTrendingUp size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Billing Type</span>
                <span className="isp__kv-value">{clean(data.billingType)?.replace(/_/g, ' ')}</span>
              </div>
            </div>
          )}
        </div>

        <div className="isp__divider" />

        {/* ── Dates ── */}
        <div className="isp__group">
          <span className="isp__group-label">Dates</span>
          <div className="isp__row isp__row--static">
            <span className="isp__row-icon"><FiClock size={12} /></span>
            <div className="isp__row-kv">
              <span className="isp__kv-label">Effective</span>
              <span className="isp__kv-value">{fmtDate(data.effectiveDate)}</span>
            </div>
          </div>
          <div className="isp__row isp__row--static">
            <span className="isp__row-icon"><FiClock size={12} /></span>
            <div className="isp__row-kv">
              <span className="isp__kv-label">Expiration</span>
              <span className="isp__kv-value">{fmtDate(data.expirationDate)}</span>
            </div>
          </div>
          {data.policyTermFormatted && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon"><FiClock size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Term</span>
                <span className="isp__kv-value">{data.policyTermFormatted}</span>
              </div>
            </div>
          )}
        </div>

        <div className="isp__divider" />

        {/* ── Financials ── */}
        <div className="isp__group">
          <span className="isp__group-label">Financials</span>
          <div className="isp__row isp__row--static">
            <span className="isp__row-icon isp__row-icon--cyan"><FiTrendingUp size={12} /></span>
            <div className="isp__row-kv">
              <span className="isp__kv-label">Total Premium</span>
              <span className="isp__kv-value">{fmtCurrency(data.totalPremium)}</span>
            </div>
          </div>
          {data.totalAgencyCommission != null && data.totalAgencyCommission > 0 && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon isp__row-icon--cyan"><FiTrendingUp size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Agency Commission</span>
                <span className="isp__kv-value">{fmtCurrency(data.totalAgencyCommission)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="isp__divider" />

        {/* ── Details ── */}
        <div className="isp__group">
          <span className="isp__group-label">Details</span>
          {clean(data.businessType) && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon"><FiGrid size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Business Type</span>
                <span className="isp__kv-value">{clean(data.businessType)}</span>
              </div>
            </div>
          )}
          {data.changeDate && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon"><FiClock size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Last Updated</span>
                <span className="isp__kv-value">{fmtDate(data.changeDate)}</span>
              </div>
            </div>
          )}
          <div className="isp__row isp__row--static">
            <span className="isp__row-icon"><FiHash size={12} /></span>
            <div className="isp__row-kv">
              <span className="isp__kv-label">Database ID</span>
              <span className="isp__kv-value isp__kv-value--mono">{data.databaseId}</span>
            </div>
          </div>
        </div>

        <div className="isp__divider" />

        {/* ── Notes ── */}
        <div className="isp__group">
          <div className="isp__group-header">
            <span className="isp__group-label">Notes</span>
            {!addingNote && (
              <button className="isp__add-note-btn" onClick={() => { setAddingNote(true); setNoteError(null); }}>
                + Add Note
              </button>
            )}
          </div>

          {addingNote && (
            <div className="isp__note-form">
              <input
                className="isp__note-input"
                placeholder="Note subject…"
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                autoFocus
              />
              {noteError && <p className="isp__note-error">{noteError}</p>}
              <div className="isp__note-actions">
                <button
                  className="isp__note-cancel-btn"
                  onClick={() => { setAddingNote(false); setNoteSubject(''); setNoteError(null); }}
                  disabled={noteSaving}
                >
                  Cancel
                </button>
                <button
                  className="isp__note-save-btn"
                  onClick={handleAddNote}
                  disabled={noteSaving || !noteSubject.trim()}
                >
                  {noteSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {noteLoad
            ? [0,1,2].map(i => <div key={i} className="isp__list-skel" style={{ width: `${75 - i * 10}%` }} />)
            : notes.length === 0
              ? <p className="isp__empty">No notes found.</p>
              : notes.map((n, i) => {
                  const subject = stripHtml(n.subject || '') || 'Untitled Note';
                  const text = stripHtml(n.note || n.description || n.notesDescription || n.text || '');
                  const noteDate = n.changeDate || n.createdDate || n.date || '';
                  const author = n.createdBy || n.userName || n.author || '';
                  // Subject and text are cut short in the list — clicking a note
                  // opens it in place to read the whole thing.
                  const expanded = expandedNotes.has(i);
                  // Many notes keep the whole note in the subject. Only show it as a
                  // heading when it is a short title over a separate body.
                  const showHeading = !!text && text !== subject && subject.length <= 80;
                  const fullText = showHeading ? text : (text && text !== subject ? `${subject}\n\n${text}` : subject);
                  const toggle = () => setExpandedNotes(prev => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i); else next.add(i);
                    return next;
                  });
                  return (
                    <div
                      key={i}
                      className={`isp__list-item isp__list-item--note${expanded ? ' isp__list-item--expanded' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={expanded}
                      title={expanded ? 'Click to collapse' : 'Click to read the full note'}
                      onClick={toggle}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                    >
                      <div className="isp__list-avatar isp__list-avatar--vehicle">
                        <FiFileText size={13} />
                      </div>
                      <div className="isp__list-meta">
                        {expanded ? (
                          <div className="isp__note-full">
                            {showHeading && <span className="isp__note-full-heading">{subject}</span>}
                            <p className="isp__note-full-text">{fullText}</p>
                          </div>
                        ) : (
                          <>
                            <span className="isp__list-title">{subject}</span>
                            {text && <div className="isp__list-sub">{text}</div>}
                          </>
                        )}
                        <div className="isp__list-details">
                          {author   && <span className="isp__list-chip isp__list-chip--type">{author}</span>}
                          {noteDate && <span className="isp__list-chip isp__list-chip--use">{new Date(noteDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
                          <span className="isp__note-toggle">
                            {expanded ? 'Show less' : 'Read more'}
                            <FiChevronDown size={12} />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
          }
          {!noteLoad && notes.length < noteTotal && (
            <button className="isp__show-more-btn" onClick={handleLoadMoreNotes} disabled={noteMoreLoad}>
              {noteMoreLoad ? 'Loading…' : `Show More (${notes.length} of ${noteTotal})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyDetailSidebarPanel;

